import React, { useState } from 'react';
import { Loader2, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';
import { MEMO_LABEL } from '../lib/memorabiliaFields';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const border = { borderColor: 'rgba(0,0,0,0.2)' };
const AI_FIELDS = ['description', 'materials', 'inscriptions', 'dimensions', 'production_methods', 'context', 'keywords', 'item_type', 'when_made', 'condition'];
const DEFAULT = ['description', 'materials', 'inscriptions'];

// One-click catalogue draft from the record's photos: pick fields → review proposals → apply into the editor form.
export default function MemorabiliaAiDraft({ rec, onApply }) {
  const { memoAiDraft } = useAuth();
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState(DEFAULT);
  const [res, setRes] = useState(null);
  const [accept, setAccept] = useState({});
  const [mode, setMode] = useState({});
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      const r = await memoAiDraft(rec.id, fields);
      setRes(r);
      setAccept(Object.fromEntries(Object.keys(r.drafts).map((k) => [k, true])));
      setMode(Object.fromEntries(Object.keys(r.drafts).map((k) => [k, r.current[k] ? 'append' : 'replace'])));
    } catch (e) { toast.error(formatApiError(e, 'Draft failed')); } finally { setBusy(false); }
  };
  const apply = () => {
    const out = {};
    Object.entries(res.drafts).forEach(([k, v]) => { if (!accept[k]) return; const cur = (res.current[k] || '').trim(); out[k] = mode[k] === 'append' && cur ? `${cur}\n\n${v}` : v; });
    onApply(out); toast.success(`${Object.keys(out).length} field(s) filled — review and Save`); setOpen(false);
  };
  const toggleField = (f) => setFields((fs) => (fs.includes(f) ? fs.filter((x) => x !== f) : [...fs, f]));
  return (
    <>
      <button onClick={() => setOpen(true)} disabled={!rec.photos?.length} title={rec.photos?.length ? 'Draft catalogue text from the linked photos' : 'Link a photo first'} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded font-title disabled:opacity-40" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="memo-ai-draft"><Sparkles size={12} /> Catalogue draft</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="memo-ai-dialog">
          <DialogHeader><DialogTitle className="font-title" style={{ color: 'var(--maroon)' }}>Catalogue draft — {rec.item_name || rec.item_id}</DialogTitle></DialogHeader>
          <p className="text-xs" style={{ color: 'var(--olive)' }}>Reads up to 4 linked photos (plus the existing record data) and drafts the chosen fields in AMaGA style. Nothing is saved until you apply the proposals and press Save.</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs" data-testid="memo-ai-fields">
            {AI_FIELDS.map((f) => <label key={f} className="inline-flex items-center gap-1"><input type="checkbox" checked={fields.includes(f)} onChange={() => toggleField(f)} data-testid={`memo-ai-field-${f}`} /> {MEMO_LABEL[f]}</label>)}
          </div>
          <button onClick={run} disabled={busy || fields.length === 0} className="self-start inline-flex items-center gap-2 rounded font-title px-3 py-1.5 text-sm disabled:opacity-50" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="memo-ai-run">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy ? 'Reading photos… (about 20 s)' : res ? 'Draft again' : 'Draft from photos'}</button>
          {res && (
            <div className="space-y-2" data-testid="memo-ai-results">
              {Object.entries(res.drafts).map(([k, v]) => (
                <div key={k} className="rounded border p-2 text-sm" style={border} data-testid={`memo-ai-result-${k}`}>
                  <div className="flex flex-wrap items-center gap-3 text-xs mb-1">
                    <label className="inline-flex items-center gap-1 font-title" style={{ color: 'var(--navy)' }}><input type="checkbox" checked={!!accept[k]} onChange={(e) => setAccept((a) => ({ ...a, [k]: e.target.checked }))} data-testid={`memo-ai-accept-${k}`} /> {MEMO_LABEL[k]}</label>
                    {res.current[k] && <select value={mode[k]} onChange={(e) => setMode((m) => ({ ...m, [k]: e.target.value }))} className="px-1.5 py-0.5 rounded border bg-white" style={border} data-testid={`memo-ai-mode-${k}`}><option value="append">Append to existing text</option><option value="replace">Replace existing text</option></select>}
                  </div>
                  <div className="whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>{v}</div>
                  {res.current[k] && <div className="mt-1 text-[11px] whitespace-pre-wrap" style={{ color: 'var(--olive)' }}>Current: {res.current[k]}</div>}
                </div>
              ))}
              {Object.keys(res.drafts).length === 0 && <p className="text-xs">No usable text was generated — try different fields.</p>}
            </div>
          )}
          <DialogFooter>
            {res && <button onClick={apply} disabled={!Object.values(accept).some(Boolean)} className="inline-flex items-center gap-2 rounded font-title px-4 py-2 text-sm disabled:opacity-50" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="memo-ai-apply"><Check size={14} /> Apply selected to record</button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
